import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { formatDistance } from 'date-fns';
import { NotificationData } from '../../models/notifications';
import { UserNotificationsStore } from './user-notifications.store';
import { TranslateService } from '@ngx-translate/core';
import { API_URL } from '@frontend-site/shared/data';
import { PollingService } from '@frontend-site/shared/core';

const MENTION_PATTERN = /\[\~.*?\]/g;
const POLLING_INTERVAL = 180000;

@Injectable()
export class UserNotificationsService {
    constructor(
        private http: HttpClient,
        private userNotificationsStore: UserNotificationsStore,
        private translate: TranslateService,
        private pollingService: PollingService,
        @Inject(API_URL) private readonly apiUrl: string
    ) {
        this.pollingService.doPolling(this.getNotificationsList(), { intervalInMS: POLLING_INTERVAL }).subscribe();
    }

    getNotificationsList(): Observable<NotificationData[]> {
        return this.http.get<NotificationData[]>(`${this.apiUrl}/notifications/userNotifications`).pipe(
            map((res) => {
                return res.map((item) => {
                    item.changedDate = formatDistance(new Date(item.changedDate), new Date(), { addSuffix: true });
                    item.contentUsers = item.content
                        .match(MENTION_PATTERN)
                        ?.map((user: string) => (user = user.substr(2, user.length - 3)));
                    const content = item.content.split(MENTION_PATTERN);
                    item.content = content[content.length - 1];

                    this.userNotificationsStore.setNotifications(res);
                    return item;
                }) as NotificationData[];
            })
        );
    }

    removeNotificationById(notification: NotificationData): Observable<number> {
        const params = new HttpParams()
            .set('subjectId', notification.subjectId)
            .set('entityId', notification.entityId)
            .set('projectId', notification.projectId);
        return this.http
            .get<number>(`${this.apiUrl}/notifications/clearUserNotification/${notification.subjectId}/0`, { params })
            .pipe(
                tap((res) =>
                    res === notification.subjectId ? this.userNotificationsStore.removeNotificationById(notification) : console.error(res)
                )
            );
    }

    removeAllNotifications(notification: NotificationData): Observable<number> {
        const params = new HttpParams()
            .set('subjectId', -1)
            .set('entityId', notification.entityId)
            .set('projectId', notification.projectId);
        return this.http
            .get<number>(`${this.apiUrl}/notifications/clearUserNotification/-1/0`, { params })
            .pipe(tap((res) => (res === -1 ? this.userNotificationsStore.removeAllNotifications() : console.error(res))));
    }

    getUserNotificationsTooltip(): Observable<string> {
        return this.userNotificationsStore.notifications$.pipe(
            switchMap((notifications) => {
                if (notifications.length > 0) {
                    return of(
                        this.translate.instant('Layouts.Masthead.USER_NOTIFICATIONS.mentionedIn') +
                            this.translate.instant('Layouts.Masthead.USER_NOTIFICATIONS.newComments', { count: notifications.length })
                    );
                } else {
                    return of(this.translate.instant('Layouts.Masthead.USER_NOTIFICATIONS.indicatorNoneTooltip'));
                }
            })
        );
    }
}
